import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, InputNumber, Modal, Row, Select } from 'antd';
import { useContext, useMemo } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { CurrencyInput } from 'app/components/commons/Currency/CurrencyInput/CurrencyInput';
import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import { DatePickerWithHelper } from 'app/components/fields/DatePickerWithHelper/DatePickerWithHelper';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { usePackages } from 'app/hooks/data/packages/usePackages';
import type { GiftCard, GiftCardEditForm } from 'app/typings/giftCard';
import { GiftCardType } from 'app/typings/giftCard';
import { formatDateWithoutHour } from 'app/utils/dates';
import { formatPackageName } from 'app/utils/packages';
import { parseInputNumber } from 'app/utils/typing';

import { EmailEditForm } from './EmailEditForm/EmailEditForm';
import './GiftCardEdit.scss';
import { PhysicalEditForm } from './PhysicalEditForm/PhysicalEditForm';
import { PrintEditForm } from './PrintEditForm/PrintEditForm';

const RULES = [{ required: true, message: 'Required' }];
const { Option } = Select;

type Props = {
  visible: boolean;
  giftCard: GiftCard;
  onCancel: () => void;
  onSubmit: (body: GiftCardEditForm) => void;
};

export const GiftCardEdit = ({
  visible,
  giftCard,
  onCancel,
  onSubmit,
}: Props) => {
  const [form] = Form.useForm();
  const { currencySymbol } = useContext(CurrencyContext);

  const isNotGiftCard =
    giftCard.giftCardType === GiftCardType.Voucher ||
    giftCard.giftCardType === GiftCardType.Incentive ||
    giftCard.giftCardType === GiftCardType.Internal ||
    giftCard.giftCardType === GiftCardType.Manual;

  const formValues = useMemo(() => {
    const commonProperties = {
      expirationDate: new Date(giftCard.expirationDate ?? new Date()),
      beneficiaryName: giftCard.beneficiaryName,
      valueType: giftCard.valueType,
    };

    if (
      giftCard.giftCardType === GiftCardType.Voucher ||
      giftCard.giftCardType === GiftCardType.Internal ||
      giftCard.giftCardType === GiftCardType.Manual
    ) {
      return {
        ...commonProperties,
        minAmount: giftCard.minAmount,
        remainingValue: giftCard.remainingValue,
      };
    }

    if (giftCard.giftCardType === GiftCardType.Incentive) {
      return {
        ...commonProperties,
        startDate: new Date(giftCard.startDate ?? new Date()),
        minAmount: giftCard.minAmount,
        maxUse: giftCard.maxUse,
        discount:
          giftCard.valueType === 'PERCENT' ? giftCard.discount : undefined,
        remainingValue:
          giftCard.valueType === 'FIX' ? giftCard.remainingValue : undefined,
        restrictedPackageIds: giftCard.restrictedPackageIds,
      };
    }

    if (giftCard.giftCardType === GiftCardType.Print) {
      return {
        ...commonProperties,
        senderName: giftCard.senderName,
        color: giftCard.color,
        message: giftCard.message,
        remainingValue: giftCard.remainingValue,
      };
    }

    if (giftCard.giftCardType === GiftCardType.Email) {
      return {
        ...commonProperties,
        senderName: giftCard.senderName,
        color: giftCard.color,
        message: giftCard.message,
        beneficiaryEmail: giftCard.beneficiaryEmail,
        billingEmail: giftCard.billingEmail,
        emailSendDate: new Date(giftCard.emailSendDate ?? new Date()),
        remainingValue: giftCard.remainingValue,
      };
    }

    return {
      ...commonProperties,
      senderName: giftCard.senderName,
      shippingFirstName: giftCard.shippingFirstName,
      shippingLastName: giftCard.shippingLastName,
      shippingStreet1: giftCard.shippingStreet1,
      shippingStreet2: giftCard.shippingStreet2,
      shippingZipCode: giftCard.shippingZipCode,
      shippingCity: giftCard.shippingCity,
      shippingCountryId: giftCard.shippingCountryId,
    };
  }, [giftCard]);

  const onFinish = (
    values: Omit<GiftCardEditForm, 'emailSendDate'> & { emailSendDate?: Date }
  ) => {
    const updatedGiftCard: GiftCardEditForm = {
      ...values,
      emailSendDate: values.emailSendDate
        ? formatDateWithoutHour(values.emailSendDate)
        : undefined,
    };

    onSubmit(updatedGiftCard);
  };

  const isFormInvalid =
    !form.isFieldsTouched() ||
    form.getFieldsError().filter(({ errors }) => errors.length > 0).length > 0;

  const specificForm = () => {
    if (isNotGiftCard) {
      return null;
    }

    if (giftCard.giftCardType === GiftCardType.Print) {
      return <PrintEditForm />;
    }

    if (giftCard.giftCardType === GiftCardType.Email) {
      return <EmailEditForm />;
    }

    return <PhysicalEditForm />;
  };

  return (
    <FormLegacyRenderProp
      form={form}
      onFinish={onFinish}
      initialValues={formValues}
    >
      {
        // This form relies on the render prop syntax to get access to updated form values during render.
        // TODO try to make this form work without the render prop syntax
        (currentFormValues) => (
          <Modal
            width={660}
            onCancel={onCancel}
            okText="Save"
            open={visible}
            title="Edit gift card"
            centered
            className="gift-card-modal"
            footer={[
              <Button
                type="primary"
                onClick={() => form.submit()}
                key="save"
                disabled={isFormInvalid}
              >
                Save
              </Button>,
            ]}
          >
            <>
              {giftCard.giftCardType === GiftCardType.Incentive && (
                <div className="input-row">
                  <Form.Item name="startDate" label="Start date" rules={RULES}>
                    <DatePickerWithHelper />
                  </Form.Item>
                </div>
              )}
              <div className="input-row">
                <Form.Item
                  name="expirationDate"
                  label="Expiry date"
                  rules={RULES}
                >
                  <DatePickerWithHelper />
                </Form.Item>
              </div>
              <div className="input-row">
                {giftCard.valueType === 'FIX' ? (
                  <Form.Item
                    name="remainingValue"
                    label="Remaining Value"
                    rules={RULES}
                  >
                    <CurrencyInput
                      min={0}
                      currency={currencySymbol}
                      parser={parseInputNumber}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item name="discount" label="Percent" rules={RULES}>
                    <InputNumber min={0} />
                  </Form.Item>
                )}
              </div>
              {!!isNotGiftCard && (
                <div className="input-row">
                  <Form.Item
                    name="minAmount"
                    label="Minimum code use amount"
                    rules={RULES}
                  >
                    <InputNumber min={0} />
                  </Form.Item>
                </div>
              )}
              {giftCard.giftCardType === GiftCardType.Incentive && (
                <div className="input-row">
                  <Form.Item name="maxUse" label="Max use" rules={RULES}>
                    <InputNumber min={1} />
                  </Form.Item>
                </div>
              )}
              {!isNotGiftCard && (
                <div className="input-row">
                  <Form.Item
                    name="senderName"
                    label="Sender Name"
                    rules={RULES}
                  >
                    <Input />
                  </Form.Item>
                </div>
              )}
              <div className="input-row">
                <Form.Item
                  name="beneficiaryName"
                  label="Beneficiary Name"
                  rules={RULES}
                >
                  <Input />
                </Form.Item>
              </div>
              {giftCard.giftCardType === GiftCardType.Incentive && (
                <div className="input-row">
                  <PackageListFormItem />
                </div>
              )}
              {specificForm()}
            </>
          </Modal>
        )
      }
    </FormLegacyRenderProp>
  );
};

const PackageListFormItem = () => {
  const { data: packages } = usePackages();

  return (
    <Form.List name="restrictedPackageIds">
      {(fields, { add, remove }) => (
        <>
          {fields.map((field, index) => (
            <Row key={field.key} className="package-list-row-container">
              <div className="package-list-row">
                <Form.Item
                  name={[field.name]}
                  label={`Package #${index + 1}`}
                  key={field.key}
                >
                  <Select
                    className="package-list-select"
                    showSearch
                    optionFilterProp="children"
                    placeholder="Select a package from the list"
                    filterOption={(input, option) =>
                      option?.props.key
                        .toLowerCase()
                        .toString()
                        .includes(input.toLowerCase())
                    }
                  >
                    {packages?.map((data) => (
                      <Option
                        key={`${data.hotelName}-${data.id}`}
                        value={data.id}
                        className="select-item__option"
                      >
                        <div className="select-item__wrapper">
                          <div className="select-item__thumbnail">
                            <PictureComponent {...data.coverPicture} />
                          </div>
                          <div className="select-item__name">
                            {formatPackageName({
                              name: data.aliasForHotel,
                              hotelName: data.hotelName,
                            })}
                          </div>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <DeleteOutlined
                className="delete-icon"
                onClick={() => {
                  remove(field.name);
                }}
              />
            </Row>
          ))}
          <Form.Item>
            <Button onClick={add} block icon={<PlusOutlined />}>
              Add package
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );
};
